import { useTranslation } from 'react-i18next';
import type { ExtractionClassification } from '@infoscend-claims/shared';
import { ARCHETYPE_DEFINITIONS } from '../../lib/schemeArchetypes.js';

// ── Confidence badge ──────────────────────────────────────────────────────────

function ConfidenceChip({ confidence }: { confidence: 'HIGH' | 'MEDIUM' | 'LOW' }) {
  const { t } = useTranslation();
  const styles = {
    HIGH:   'text-copper bg-copper/10 border-copper/30',
    MEDIUM: 'text-slate-600 bg-slate-100 border-slate-200',
    LOW:    'text-amber-700 bg-amber-50 border-amber-300',
  } as const;
  const icon = { HIGH: '✓', MEDIUM: '~', LOW: '⚠' } as const;
  const label = {
    HIGH:   t('admin.extraction.confidence.high',   { defaultValue: 'HIGH CONFIDENCE' }),
    MEDIUM: t('admin.extraction.confidence.medium', { defaultValue: 'MEDIUM CONFIDENCE' }),
    LOW:    t('admin.extraction.confidence.low',    { defaultValue: 'LOW CONFIDENCE' }),
  } as const;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-semibold tracking-wider whitespace-nowrap ${styles[confidence]}`}>
      <span>{icon[confidence]}</span>
      {label[confidence]}
    </span>
  );
}

// ── Main card ─────────────────────────────────────────────────────────────────

export function ExtractionClassificationCard({
  classification,
}: {
  classification: ExtractionClassification | null | undefined;
}) {
  const { t } = useTranslation();

  if (!classification) return null;

  const { archetypeKey, confidence, signals, alternates } = classification;

  const archDef = archetypeKey ? ARCHETYPE_DEFINITIONS.find((a) => a.key === archetypeKey) : null;
  const archetypeName = archDef && archetypeKey
    ? t(`admin.schemes.archetypes.${archetypeKey}.name`, { defaultValue: archetypeKey.replace(/_/g, ' ') })
    : (archetypeKey?.replace(/_/g, ' ') ?? '—');

  const cardBorder = confidence === 'LOW' && alternates.length >= 2
    ? 'border-amber-300'
    : 'border-hairline';

  return (
    <div className={`bg-white border ${cardBorder} rounded p-5 space-y-4`}>

      {/* ── Headline row ── */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 shrink-0">
            {t('admin.extraction.aiClassifiedAs', { defaultValue: 'AI classified this as' })}
          </span>
          <span className="font-serif italic text-ink text-base leading-tight">{archetypeName}</span>
        </div>
        <ConfidenceChip confidence={confidence} />
      </div>

      {/* ── Detected signals ── */}
      {signals.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">
            {t('admin.extraction.detectedSignals', { defaultValue: 'Detected signals' })}
          </p>
          <ul className="space-y-1.5">
            {signals.map((signal, i) => (
              <li key={i} className="flex gap-2 items-start">
                <span className="text-copper text-xs shrink-0 mt-0.5">→</span>
                <span className="font-mono text-[12px] text-slate-700 leading-snug">{signal}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Alternate archetypes — VISUAL ONLY, no interaction ── */}
      {alternates.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">
            {t('admin.extraction.looksWrongConsider', { defaultValue: 'Looks wrong? Consider:' })}
          </p>
          <div className="flex flex-wrap gap-2">
            {alternates.map((alt) => {
              const altDef = ARCHETYPE_DEFINITIONS.find((a) => a.key === alt.archetypeKey);
              const altName = altDef
                ? t(`admin.schemes.archetypes.${alt.archetypeKey}.name`, { defaultValue: alt.archetypeKey.replace(/_/g, ' ') })
                : alt.archetypeKey.replace(/_/g, ' ');
              return (
                <div
                  key={alt.archetypeKey}
                  title={alt.reasoning}
                  className="group relative inline-flex items-center gap-1.5 px-3 py-1 rounded border border-dashed border-slate-200 bg-slate-50 text-xs text-slate-400 cursor-not-allowed select-none"
                  aria-disabled="true"
                >
                  {altName}
                  <span className="text-[9px] uppercase tracking-wider text-slate-300">soon</span>
                  {/* hover tooltip */}
                  <span className="pointer-events-none absolute bottom-full left-0 mb-2 z-20 hidden group-hover:block w-64 rounded border border-hairline bg-white px-3 py-2 text-[11px] text-slate-600 shadow-md leading-snug">
                    {alt.reasoning}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-slate-400 mt-2 italic">
            {t('admin.extraction.alternatesComingSoon', { defaultValue: 'One-click archetype switching ships after Friday demo.' })}
          </p>
        </div>
      )}
    </div>
  );
}
